import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateText } from '../services/translationService';
import {
    FaPaperPlane, FaImage, FaSpinner, FaTimesCircle,
    FaComments, FaUserAlt, FaIndustry, FaSmile,
    FaChevronLeft, FaChevronRight, FaEnvelope, FaCheck,
    FaClock
} from 'react-icons/fa';
import EmojiPicker from 'emoji-picker-react';
import '../styles/CaseChat.css';

const CaseChat = ({
    orderId,
    caseInfo = null,
    onPreviousCase = null,
    onNextCase = null,
    hasPrevious = false,
    hasNext = false,
    currentCaseIndex = null,
    totalCasesCount = null,
    onMessagesRead = null,
    onMarkAsUnread = null,
    onPendingReplyChange = null,
    onClose = null
}) => {
    const { isAdmin, isProducer, user } = useAuth();
    const { t, isChinese } = useLanguage();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [justMarkedUnread, setJustMarkedUnread] = useState(false);
    const [pendingReply, setPendingReply] = useState(false);
    const [isClosedCase, setIsClosedCase] = useState(false);
    const [translatedMessages, setTranslatedMessages] = useState({});
    const [translatingId, setTranslatingId] = useState(null);
    const [showOriginalMap, setShowOriginalMap] = useState({});
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const emojiPickerRef = useRef(null);

    // Auto-detect sender based on role
    const sender = isProducer ? 'partner' : 'lab';

    const handleTranslateMessage = async (msgId, text) => {
        if (translatedMessages[msgId]) {
            // Toggle view between translated and original
            setShowOriginalMap(prev => ({ ...prev, [msgId]: !prev[msgId] }));
            return;
        }

        try {
            setTranslatingId(msgId);
            const targetLang = isChinese ? 'zh-CN' : 'fr';
            const translated = await translateText(text, targetLang);
            setTranslatedMessages(prev => ({ ...prev, [msgId]: translated }));
            setShowOriginalMap(prev => ({ ...prev, [msgId]: false }));
        } catch (e) {
            console.error('Translation error:', e);
        } finally {
            setTranslatingId(null);
        }
    };

    // Load messages, reset marked unread, and fetch pending reply status on case change
    useEffect(() => {
        setJustMarkedUnread(false);
        loadMessages();

        const loadPendingStatus = async () => {
            if (!orderId) return;
            try {
                const { data } = await supabase
                    .from('cases')
                    .select('priority, status')
                    .eq('id', orderId)
                    .single();
                if (data) {
                    const st = (data.status || '').toLowerCase().trim();
                    const isClosed = st === 'livré' || st === 'livre' || st === 'facturé' || st === 'facture';
                    setIsClosedCase(isClosed);
                    setPendingReply(!isClosed && data.priority === 'pending_reply');
                }
            } catch (err) {
                console.error('Erreur chargement pending status:', err);
            }
        };
        loadPendingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    // Auto-scroll to bottom
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Mark messages as read when chat is opened (unless user just marked as unread)
    useEffect(() => {
        if (!orderId || loading || justMarkedUnread) return;
        markMessagesAsRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, loading, justMarkedUnread]);

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            imagePreviews.forEach(p => URL.revokeObjectURL(p.url));
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close emoji picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        };
        if (showEmojiPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showEmojiPicker]);

    // Render message text with clickable links
    const renderMessageText = (text) => {
        if (!text) return null;
        // URL regex that matches http(s), www, and common protocols
        const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
        const parts = text.split(urlRegex);

        return parts.map((part, index) => {
            if (urlRegex.test(part)) {
                const href = part.startsWith('www.') ? `https://${part}` : part;
                return (
                    <a
                        key={index}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bubble-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {part}
                    </a>
                );
            }
            return part;
        });
    };

    // Handle emoji selection
    const handleEmojiClick = (emojiData) => {
        setNewMessage(prev => prev + emojiData.emoji);
        setShowEmojiPicker(false);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const markMessagesAsRead = async () => {
        if (!user?.id || justMarkedUnread) return;
        try {
            // Only get message IDs sent by the other party
            const targetSender = isAdmin ? 'partner' : 'lab';
            const { data: orderMessages, error: fetchErr } = await supabase
                .from('case_messages')
                .select('id')
                .eq('order_id', orderId)
                .eq('sender', targetSender);

            if (fetchErr || !orderMessages?.length) return;

            // Insert read records for current user (ignore duplicates)
            const reads = orderMessages.map(msg => ({
                message_id: msg.id,
                user_id: user.id
            }));

            await supabase
                .from('message_reads')
                .upsert(reads, { onConflict: 'message_id,user_id', ignoreDuplicates: true });

            if (onMessagesRead) {
                onMessagesRead(orderId);
            }
            window.dispatchEvent(new CustomEvent('messages-read', { detail: { orderId } }));
        } catch (err) {
            console.error('Error marking messages as read:', err);
        }
    };

    const markAsUnread = async () => {
        if (!user?.id || !orderId) return;
        try {
            const targetSender = isAdmin ? 'partner' : 'lab';
            const { data: orderMessages, error: fetchErr } = await supabase
                .from('case_messages')
                .select('id')
                .eq('order_id', orderId)
                .eq('sender', targetSender);

            if (fetchErr || !orderMessages?.length) return;

            const msgIds = orderMessages.map(msg => msg.id);

            const { error: delErr } = await supabase
                .from('message_reads')
                .delete()
                .eq('user_id', user.id)
                .in('message_id', msgIds);

            if (delErr) {
                console.error('Erreur markAsUnread:', delErr);
            } else {
                setJustMarkedUnread(true);
                if (onMarkAsUnread) {
                    onMarkAsUnread(orderId);
                }
                window.dispatchEvent(new CustomEvent('messages-unread', { detail: { orderId } }));
            }
        } catch (err) {
            console.error('Error marking as unread:', err);
        }
    };

    const togglePendingReply = async () => {
        if (!orderId) return;
        const nextState = !pendingReply;
        setPendingReply(nextState);
        try {
            await supabase
                .from('cases')
                .update({ priority: nextState ? 'pending_reply' : 'normal' })
                .eq('id', orderId);
            if (onPendingReplyChange) {
                onPendingReplyChange(orderId, nextState);
            }
        } catch (err) {
            console.error('Erreur toggle pending reply:', err);
        }
    };

    const loadMessages = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('case_messages')
                .select('*')
                .eq('order_id', orderId)
                .order('created_at', { ascending: true });

            if (error) {
                console.error('Erreur chargement messages:', error);
                setMessages([]);
            } else {
                setMessages(data || []);
            }
        } catch (err) {
            console.error('Erreur:', err);
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    const addImages = (files) => {
        if (!files || !files.length) return;

        const filesArray = Array.from(files).filter(f => f && f.type && f.type.startsWith('image/'));
        if (!filesArray.length) return;

        // Check total count (existing + new, max 10)
        const totalCount = imageFiles.length + filesArray.length;
        if (totalCount > 10) {
            alert(`Maximum 10 photos par message. Vous en avez déjà ${imageFiles.length}.`);
            return;
        }

        // Check each file size
        const validFiles = [];
        for (const file of filesArray) {
            if (file.size > 10 * 1024 * 1024) {
                alert(`"${file.name || 'Image'}" est trop volumineuse (max 10 MB par photo)`);
            } else {
                // Give a clean name to clipboard captures if missing or generic
                let cleanFile = file;
                if (!file.name || file.name === 'image.png' || file.name === 'blob') {
                    const ext = (file.type && file.type.split('/')[1]) || 'png';
                    cleanFile = new File([file], `capture_${Date.now()}_${Math.random().toString(36).substring(2, 6)}.${ext}`, {
                        type: file.type || 'image/png'
                    });
                }
                validFiles.push(cleanFile);
            }
        }

        if (!validFiles.length) return;

        // Add to existing files
        setImageFiles(prev => [...prev, ...validFiles]);

        // Generate previews
        const newPreviews = validFiles.map(file => ({
            url: URL.createObjectURL(file),
            name: file.name
        }));
        setImagePreviews(prev => [...prev, ...newPreviews]);
    };

    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files || []);
        addImages(files);
    };

    const handlePaste = (e) => {
        const clipboardData = e.clipboardData;
        if (!clipboardData) return;

        const filesToProcess = [];

        // Check clipboardData.items first (standard for screenshots/Snipping Tool)
        if (clipboardData.items && clipboardData.items.length) {
            for (let i = 0; i < clipboardData.items.length; i++) {
                const item = clipboardData.items[i];
                if (item.type && item.type.startsWith('image/')) {
                    const blob = item.getAsFile();
                    if (blob) {
                        filesToProcess.push(blob);
                    }
                }
            }
        }

        // Fallback to clipboardData.files if items didn't find any image
        if (!filesToProcess.length && clipboardData.files && clipboardData.files.length) {
            for (let i = 0; i < clipboardData.files.length; i++) {
                const file = clipboardData.files[i];
                if (file.type && file.type.startsWith('image/')) {
                    filesToProcess.push(file);
                }
            }
        }

        if (filesToProcess.length > 0) {
            e.preventDefault();
            addImages(filesToProcess);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.files?.length) {
            addImages(e.dataTransfer.files);
        }
    };

    const removeImagePreview = (index) => {
        URL.revokeObjectURL(imagePreviews[index].url);
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        if (imageFiles.length <= 1 && fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeAllPreviews = () => {
        imagePreviews.forEach(p => URL.revokeObjectURL(p.url));
        setImageFiles([]);
        setImagePreviews([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSend = async () => {
        if (!newMessage.trim() && imageFiles.length === 0) return;

        try {
            setSending(true);

            // Upload all images
            const uploadedUrls = [];
            for (const file of imageFiles) {
                const fileExt = file.name.split('.').pop();
                const fileName = `chat_${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
                const filePath = `orders/${orderId}/chat/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('fiches')
                    .upload(filePath, file, { upsert: true });

                if (!uploadError) {
                    const { data: urlData } = supabase.storage
                        .from('fiches')
                        .getPublicUrl(filePath);
                    if (urlData?.publicUrl) {
                        uploadedUrls.push(urlData.publicUrl);
                    }
                } else {
                    console.error('Upload error for', file.name, ':', uploadError);
                }
            }

            // Determine image_url: single URL or JSON array for multiple
            let imageUrl = null;
            if (uploadedUrls.length === 1) {
                imageUrl = uploadedUrls[0];
            } else if (uploadedUrls.length > 1) {
                // Store multiple URLs as a JSON array string
                imageUrl = JSON.stringify(uploadedUrls);
            }

            // Insert message with read flags
            const { data, error } = await supabase
                .from('case_messages')
                .insert([{
                    order_id: orderId,
                    sender: sender,
                    message: newMessage.trim() || null,
                    image_url: imageUrl,
                    read_by_lab: sender === 'lab',
                    read_by_partner: sender === 'partner'
                }])
                .select()
                .single();

            if (error) {
                console.error('Erreur envoi message:', error);
                alert('Erreur lors de l\'envoi du message');
            } else {
                setMessages(prev => [...prev, data]);
                setNewMessage('');
                removeAllPreviews();

                // Clear pending reply automatically when a response is sent
                if (pendingReply) {
                    setPendingReply(false);
                    supabase
                        .from('cases')
                        .update({ priority: 'normal' })
                        .eq('id', orderId)
                        .then(() => {
                            if (onPendingReplyChange) onPendingReplyChange(orderId, false);
                        });
                }
            }
        } catch (err) {
            console.error('Erreur:', err);
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit', month: '2-digit'
        }) + ' à ' + date.toLocaleTimeString('fr-FR', {
            hour: '2-digit', minute: '2-digit'
        });
    };

    const handleDeleteMessage = async (msgId) => {
        if (!window.confirm('Supprimer ce message ?')) return;
        const { error } = await supabase
            .from('case_messages')
            .delete()
            .eq('id', msgId);
        if (!error) {
            setMessages(prev => prev.filter(m => m.id !== msgId));
        }
    };

    // Parse image URLs - handles both single URL and JSON array format
    const getImageUrls = (imageUrlField) => {
        if (!imageUrlField) return [];
        // Try JSON parse (for multiple images stored as JSON array)
        if (imageUrlField.startsWith('[')) {
            try {
                return JSON.parse(imageUrlField);
            } catch {
                return [imageUrlField];
            }
        }
        return [imageUrlField];
    };

    if (loading) {
        return (
            <div className="chat-loading">
                <FaSpinner className="chat-spinner" />
                <p>Chargement de la discussion...</p>
            </div>
        );
    }

    return (
        <div
            className="case-chat-container"
            onPaste={handlePaste}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {/* Chat Header */}
            <div className="chat-header">
                <div className="chat-header-info">
                    <FaComments className="me-1" />
                    {caseInfo ? (
                        <div className="chat-header-case-details">
                            <span className="chat-case-number-badge">{caseInfo.caseNumber || `#${orderId.substring(0, 8).toUpperCase()}`}</span>
                            <span className="chat-case-title">{caseInfo.patientName ? `Cas de ${caseInfo.patientName}` : 'Discussion'}</span>
                        </div>
                    ) : (
                        <span className="fw-bold">Discussion sous-traitance</span>
                    )}
                    <span className="chat-msg-count">{messages.length} msg{messages.length > 1 ? 's' : ''}</span>
                </div>

                <div className="chat-header-right">
                    {/* Navigation between cases */}
                    {totalCasesCount > 1 && (
                        <div className="chat-nav-container" title="Enchaîner les dossiers avec messages">
                            <button
                                type="button"
                                className="chat-nav-btn"
                                onClick={onPreviousCase}
                                disabled={!hasPrevious}
                                title="Cas précédent"
                            >
                                <FaChevronLeft size={10} />
                                <span className="d-none d-sm-inline">Préc.</span>
                            </button>
                            <span className="chat-nav-counter">
                                {currentCaseIndex !== null ? `${currentCaseIndex + 1}/${totalCasesCount}` : ''}
                            </span>
                            <button
                                type="button"
                                className="chat-nav-btn"
                                onClick={onNextCase}
                                disabled={!hasNext}
                                title="Cas suivant"
                            >
                                <span className="d-none d-sm-inline">Suiv.</span>
                                <FaChevronRight size={10} />
                            </button>
                        </div>
                    )}

                    {/* Mark as Unread Button */}
                    <button
                        type="button"
                        className={`chat-unread-btn ${justMarkedUnread ? 'success' : ''}`}
                        onClick={markAsUnread}
                        title={justMarkedUnread ? "Marqué comme non lu !" : "Marquer cette discussion comme non lue pour la traiter plus tard"}
                    >
                        {justMarkedUnread ? (
                            <>
                                <FaCheck size={11} />
                                <span>Non lu</span>
                            </>
                        ) : (
                            <>
                                <FaEnvelope size={11} />
                                <span>Non lu</span>
                            </>
                        )}
                    </button>

                    {/* Manual Pending Reply Toggle Button (only for active cases: Reçu, Production, En attente) */}
                    {!isClosedCase && (
                        <button
                            type="button"
                            className={`chat-pending-btn ${pendingReply ? 'active' : ''}`}
                            onClick={togglePendingReply}
                            title={pendingReply ? "Ce cas est marqué 'En attente de réponse' - Cliquer pour retirer" : "Mettre ce cas en attente de réponse"}
                        >
                            <FaClock size={11} />
                            <span>{pendingReply ? '⏳ En attente' : 'Mettre en attente'}</span>
                        </button>
                    )}

                    <div className="chat-sender-indicator">
                        {isProducer ? (
                            <span className="sender-label partner">
                                <FaUserAlt className="me-1" /> Sous-traitant
                            </span>
                        ) : (
                            <span className="sender-label lab">
                                <FaIndustry className="me-1" /> Labo
                            </span>
                        )}
                    </div>

                    {onClose && (
                        <button type="button" className="chat-close-btn" onClick={onClose} title="Fermer la discussion">
                            <FaTimesCircle />
                        </button>
                    )}
                </div>
            </div>

            {/* Messages Area */}
            <div className="chat-messages-area">
                {messages.length === 0 ? (
                    <div className="chat-empty">
                        <FaComments size={40} className="mb-3" />
                        <p className="fw-bold">{t('chat.empty_title', 'Aucun échange pour le moment')}</p>
                        <p className="text-muted small">
                            {isProducer
                                ? t('chat.empty_producer', 'Posez une question au laboratoire concernant ce cas')
                                : t('chat.empty_lab', 'Commencez la discussion avec votre sous-traitant')}
                        </p>
                    </div>
                ) : (
                    messages.map((msg) => {
                        const msgImages = getImageUrls(msg.image_url);
                        const isTranslated = !!translatedMessages[msg.id];
                        const showOrig = showOriginalMap[msg.id];
                        return (
                            <div
                                key={msg.id}
                                className={`chat-bubble ${msg.sender === sender ? 'sent' : 'received'}`}
                            >
                                <div className="bubble-sender">
                                    {msg.sender === 'lab' 
                                        ? (isChinese ? '🏭 法国技工所 ENOVADENT' : '🏭 Labo') 
                                        : (isChinese ? '👤 生产工厂 (您)' : '👤 Sous-traitant')}
                                </div>
                                {msgImages.length > 0 && (
                                    <div className={`bubble-images ${msgImages.length > 1 ? 'bubble-images-grid' : ''}`}>
                                        {msgImages.map((url, idx) => (
                                            <div className="bubble-image" key={idx}>
                                                <img
                                                    src={url}
                                                    alt={`Photo ${idx + 1}`}
                                                    onClick={() => window.open(url, '_blank')}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {msg.message && (
                                    <div className="bubble-text">
                                        {renderMessageText(msg.message)}
                                        {isTranslated && !showOrig && (
                                            <div className="chat-translated-box">
                                                <div className="chat-translated-tag">
                                                    🌐 {isChinese ? '智能翻译 (中文)' : 'Traduction'}
                                                </div>
                                                <div>{renderMessageText(translatedMessages[msg.id])}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="bubble-meta">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="bubble-time">{formatTime(msg.created_at)}</span>
                                        {msg.message && (
                                            <button
                                                type="button"
                                                className="bubble-translate-btn"
                                                onClick={() => handleTranslateMessage(msg.id, msg.message)}
                                                title={isChinese ? "智能翻译" : "Traduire"}
                                            >
                                                {translatingId === msg.id 
                                                    ? (isChinese ? '翻译中...' : 'Traduction...') 
                                                    : isTranslated
                                                        ? (showOrig ? (isChinese ? '🌐 看译文' : '🌐 Traduction') : (isChinese ? '📄 隐藏译文' : '📄 Masquer'))
                                                        : (isChinese ? '🌐 翻译' : '🌐 Traduire')}
                                            </button>
                                        )}
                                    </div>
                                    {isAdmin && (
                                        <button
                                            className="bubble-delete"
                                            onClick={() => handleDeleteMessage(msg.id)}
                                            title="Supprimer"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Image Previews - Multiple */}
            {imagePreviews.length > 0 && (
                <div className="chat-image-preview">
                    <div className="preview-images-list">
                        {imagePreviews.map((preview, idx) => (
                            <div className="preview-item" key={idx}>
                                <img src={preview.url} alt={`Aperçu ${idx + 1}`} />
                                <button className="preview-remove-item" onClick={() => removeImagePreview(idx)}>
                                    <FaTimesCircle />
                                </button>
                            </div>
                        ))}
                    </div>
                    {imagePreviews.length > 1 && (
                        <button className="preview-remove-all" onClick={removeAllPreviews}>
                            Tout supprimer
                        </button>
                    )}
                    <span className="preview-count">{imagePreviews.length} photo{imagePreviews.length > 1 ? 's' : ''}</span>
                </div>
            )}

            {/* Input Area */}
            <div className="chat-input-area">
                <button
                    className="chat-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title={isChinese ? "添加图片 (支持 Ctrl+V 粘贴截图)" : "Joindre des photos (ou Ctrl+V pour coller une capture)"}
                >
                    <FaImage />
                    {imageFiles.length > 0 && (
                        <span className="attach-badge">{imageFiles.length}</span>
                    )}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                />
                <div className="chat-input-wrapper">
                    <textarea
                        className="chat-input"
                        placeholder={isProducer ? (isChinese ? '向技工所提问关于此工单的问题... (可按 Ctrl+V 粘贴截图)' : 'Poser une question sur ce cas... (Ctrl+V pour coller une capture)') : (isChinese ? '输入消息... (可按 Ctrl+V 粘贴截图)' : 'Écrire un message... (ou Ctrl+V pour coller une capture)')}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onPaste={handlePaste}
                        rows={1}
                    />
                    <button
                        className="chat-emoji-btn"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        title={isChinese ? "添加表情" : "Ajouter un emoji"}
                        type="button"
                    >
                        <FaSmile />
                    </button>
                    {showEmojiPicker && (
                        <div className="emoji-picker-container" ref={emojiPickerRef}>
                            <EmojiPicker
                                onEmojiClick={handleEmojiClick}
                                width={300}
                                height={350}
                                searchPlaceholder={isChinese ? "搜索表情..." : "Rechercher..."}
                                previewConfig={{ showPreview: false }}
                            />
                        </div>
                    )}
                </div>
                <button
                    className="chat-send-btn"
                    onClick={handleSend}
                    disabled={sending || (!newMessage.trim() && imageFiles.length === 0)}
                >
                    {sending ? <FaSpinner className="chat-spinner" /> : <FaPaperPlane />}
                </button>
            </div>
        </div>
    );
};

export default CaseChat;
